import { Avatar, Box, Button, CircularProgress, Tooltip, Typography, useTheme } from '@mui/material'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'
import dayjs from 'dayjs'

import HybridClassAttendanceConfirmDialog from '../../components/webinar-room/meeting-details/HybridClassAttendanceConfirmDialog'
import AddQuizFromClassDialog from '../../components/exams-components/quiz-questionbank/class-quizzes/AddQuizFromClassDialog'
import OnlineSessionAttendanceDetails from '../../components/webinar-room/meeting-details/OnlineSessionAttendanceDetails'
import OnlineSessionRecordingsDetails from '../../components/webinar-room/meeting-details/OnlineSessionRecordingsDetails'
import OnlineSessionFeedbacksDetails from '../../components/webinar-room/meeting-details/OnlineSessionFeedbacksDetails'
import { formatDate, getNameInitials, Icons, leadingZeroFormatter, USER_CARD_COLOR_SETS } from '../../utils/utilities'
import OnlineSessionQuizDetails from '../../components/webinar-room/meeting-details/OnlineSessionQuizDetails'
import AddNewRecordingButton from '../../components/webinar-room/meeting-details/AddNewRecordingButton'
import OnlineSessionPolls from '../../components/webinar-room/meeting-details/OnlineSessionPolls'
import MeetingDetailsCopyDialog from '../../components/webinar-room/MeetingDetailsCopyDialog'
import MeetingAccessEditDialog from '../../components/webinar-room/MeetingAccessEditDialog'
import EditMeetingDetails from '../../components/webinar-room/EditMeetingDetails'
import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import { useOnlineSessionsStore } from '../../store/onlinesessions.store'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import CustomTable from '../../components/common/custom-table/CustomTable'
import { getTransformedMeetingType } from '../../utils/meeting-utilities'
import { useQuizAndQuestionbankStore } from '../../store/quiz.store'
import BreadcrumbList from '../../components/common/BreadcrumbList'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import { useSettingsStore } from '../../store/settings.store'
import CustomTabs from '../../components/common/CustomTabs'
import CustomChip from '../../components/common/CustomChip'
import CountCard from '../../components/common/CountCard'
import { useUserStore } from '../../store/user.store'
import { useRoomStore } from '../../store/room.store'
import api from '../../service/api'

const UserNameCard = ( { user, index } ) => {

    const bgcolor = USER_CARD_COLOR_SETS[index].primary

    return (
        <Box display="flex" alignItems="center" gap="10px" >
            <Avatar sx={{ fontSize: "10px", bgcolor, color: "#fff", width: "20px", height: "20px", }} >{getNameInitials( user.user_name )}</Avatar>
            <Typography fontSize="14px" fontWeight={500}>{user.user_name}</Typography>
        </Box>
    )
}

const hostsTableColumns = [
    { name: "Sl. No.", field: "index", index: true, width: "90px", maxWidth: "90px", align: "center" },
    { name: "Host name", field: "user_name", renderCell: ( user, idx ) => <UserNameCard key={user.user_id} index={idx} user={user} />, width: "150px" },
    { name: "E-mail ID", field: "user_email", width: "150px" },
]

const OnlineMeetingDetails = observer( () => {

    const [showDeleteMeetingConfirmationDialog, setShowDeleteMeetingConfirmationDialog] = useState( false )
    const [attendanceReviewDialogOpen, setAttendanceReviewDialogOpen] = useState( false )
    const [syncingAttendance, setSyncingAttendance] = useState( false )
    const [quizDetailsMode, setQuizDetailsMode] = useState( false )
    const [showCopyDialog, setShowCopyDialog] = useState( false )
    const [addQuizMode, setAddQuizMode] = useState( false )
    const [editMode, setEditMode] = useState( false )
    const [deleting, setDeleting] = useState( false )

    const { palette, border, layout } = useTheme()
    const { meeting_id } = useParams()
    const { pathname } = useLocation()
    const navigate = useNavigate()

    const [tabValue, setTabValue] = useState( pathname.includes( "hybrid-class" ) ? "recordings" : 'attendance' )


    const OnlineSessionsStore = useOnlineSessionsStore()
    const QuizStore = useQuizAndQuestionbankStore()
    const SettingsStore = useSettingsStore()
    const UserStore = useUserStore()
    const RoomStore = useRoomStore()

    const maxExtendableTime = SettingsStore.getUseSettings?.["hybrid_class_max_video_extend_time"] || '17:20'
    const onlineSessionQuizInstance = OnlineSessionsStore.getOnlineSessionQuizInstance
    const videoWatchAttendance = OnlineSessionsStore.getVideosWatchAttendance
    const meetingRecordings = OnlineSessionsStore.getStreamRecordings
    const attendance = OnlineSessionsStore.getStreamAttendance
    const meeting = OnlineSessionsStore.getSessionMetadata

    const recordingsFecthError = OnlineSessionsStore.getErrors.onlineSessionRecordings
    const error = OnlineSessionsStore.getErrors.onlineSession

    const { initialLoad: loadingRecordings } = OnlineSessionsStore.getRecordingsLoadingStatus
    const { initialLoad: loading, refreshing } = OnlineSessionsStore.getMetadataLoadingStatus

    const auditingPage = pathname.match( /(admin|audit)/ )

    const maxExtendTimeDetails = useMemo( () => {
        const hourAndMinute = maxExtendableTime.split( ":" )
        const [hour, minute] = hourAndMinute.map( i => +i )
        if ( hour && Number.isInteger( hour ) && hour > 0 && hour <= 24 && Number.isInteger( minute ) && minute >= 0 && minute <= 60 ) {
            return { hour24: leadingZeroFormatter.format( hour ), minute, hour12: leadingZeroFormatter.format( ( hour % 12 ) || 12 ) }
        } else {
            return { hour: 17, minute: 20 }
        }
    }, [maxExtendableTime] )


    const tabs = useMemo( () => {

        if ( !meeting ) return []

        const tabOptions = []

        if ( ["hybrid-class"].includes( meeting.meeting_type ) )
            tabOptions.push(
                { label: "Recordings", value: "recordings", icon: Icons.default.VideocamIcon, count: meetingRecordings.length },
            )


        tabOptions.push( { label: "Participants", value: "attendance", icon: Icons.default.Group, count: new Set( [...attendance.map( u => u.user_id ), ...videoWatchAttendance.map( v => v.user_id )] ).size } )

        if ( ["on-going", "ended"].includes( meeting.status ) ) {
            tabOptions.push( { label: "Feedback", value: "feedback", icon: Icons.default.FeedbackIcon } )
            tabOptions.push( { label: "Polls", value: "polls", icon: Icons.default.Checklist } )
            // tabOptions.push( { label: "Virtual board notes", value: "vbnotes", icon: Icons.default.AssignmentIcon } )
        }

        if ( ["class"].includes( meeting.meeting_type ) )
            tabOptions.push(
                { label: "Recordings", value: "recordings", icon: Icons.default.VideocamIcon, count: meetingRecordings.length },
            )


        if ( !["hybrid-class", "class"].includes( meeting.meeting_type ) )
            tabOptions.push(
                { label: "Recordings", value: "recordings", icon: Icons.default.VideocamIcon, count: meetingRecordings.length },
                { label: "Hosts", value: "hosts", icon: Icons.default.User },
            )

        return tabOptions
    }, [meetingRecordings, attendance, meeting, videoWatchAttendance] )

    const classQuizInstanceDetails = useMemo( () => {
        const details = {
            ...( QuizStore.getClassQuizzes[meeting.time_table_id]?.[0] || {} )
        }

        const groupId = meeting.time_table_class_details?.batch_short_name !== "NA" ? String( meeting.time_table_class_details?.batch_assignment_id ) : `${meeting.time_table_class_details?.section_assignment_id}-${meeting.time_table_class_details?.section_short_name}`
        details.access_group = UserStore.getUsersAllBatchesAndSectionList?.find( item => String( item.id ) === groupId )
        return details
    }, [QuizStore.getClassQuizzes, UserStore, meeting] )

    const retryAttendanceSync = async () => {
        try {
            setSyncingAttendance( true )
            await api.onlineMeetings.pushAttendanceToERP( meeting_id )
            await fetchMeeting( true )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong while syncing attendance please try again." )
        } finally {
            setSyncingAttendance( false )
        }
    }

    const getClasses = useCallback( async ( refresh = false ) => {
        if ( QuizStore.getErrors.classQuizzes )
            await QuizStore.fetchClassAssociatedQuizInstances()
        if ( refresh || !Boolean( RoomStore.getRooms[meeting.time_table_class_details.selected_data] ) ) {
            await RoomStore.fetchRooms( meeting.time_table_class_details.selected_data )
        }
    }, [RoomStore, meeting, QuizStore] )

    const handleMoveBack = () => {
        let url = `/${["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role?.toUpperCase() ) ? "admin" : "audit"}`
        url += pathname.includes( "online-classes" ) ? `/online-classes/list` : pathname.includes( "hybrid-classes" ) ? `/hybrid-classes/list` : `/meetings/list`
        navigate( url )
    }

    const handleEditAccessDialogClose = () => {
        fetchMeeting()
        setEditMode( false )
    }

    const deleteOnlineMeeting = async () => {
        try {
            setDeleting( true )
            await api.onlineMeetings.deleteMeeting( meeting_id )
            if ( ["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role?.toUpperCase() ) ) {
                navigate( `/admin/${meeting.meeting_type === 'class' ? "online-classes" : meeting.meeting_type === 'hybrid-class' ? "hybrid-classes" : "meetings"}/list` )
            } else {
                navigate( `/faculty/${["discussion", "webinar"].includes( meeting.meeting_type ) ? "meetings" : ["hybrid-class"].includes( meeting.meeting_type ) ? "hybrid-classes" : "online-classes"}` )
            }
        } catch ( err ) {
            toast( err?.response?.data?.message || "Failed to delete the online meeting." )
        } finally {
            setDeleting( false )
        }
    }

    const fetchMeeting = useCallback( async ( refetch = false ) => {
        await OnlineSessionsStore.fetchOnlineSessionMetadata( meeting_id )
        const type = OnlineSessionsStore.getSessionMetadata.meeting_type
        if ( type === "hybrid-class" ) {
            if ( !refetch )
                setTabValue( "recordings" )
            OnlineSessionsStore.fetchHybridClassAccessTimeExtensionRequests( meeting_id )
            OnlineSessionsStore.fetchOnlineSessionAttendance( meeting_id )
        } else {
            OnlineSessionsStore.fetchOnlineSessionRecordings( meeting_id )
        }
    }, [meeting_id, OnlineSessionsStore] )

    const fetchQuizDetails = useCallback( async () => {
        if ( QuizStore.getErrors.classQuizzes ) {
            await QuizStore.fetchClassAssociatedQuizInstances()
        }
    }, [QuizStore] )

    useEffect( () => {
        fetchMeeting()
        fetchQuizDetails()

    }, [fetchMeeting, fetchQuizDetails] )



    return (
        <Box padding="20px" minWidth={0} position="relative" display={auditingPage ? "flex" : "block"} flexDirection="column" overflow={auditingPage ? "auto" : "auto"} flexGrow={auditingPage ? "1" : 1}>

            {auditingPage && <Button variant='outlined' color='greyed' disableElevation onClick={handleMoveBack} startIcon={Icons.default.ArrowBack} sx={{ width: "fit-content", marginBottom: "20px" }}>
                Back to {pathname.includes( 'classes' ) ? "Classes" : "Meetings and webinars"}
            </Button>}

            {!auditingPage && !error && <BreadcrumbList items={[
                {
                    label: meeting ? `${getTransformedMeetingType( meeting?.meeting_type, { forDisplay: true, plural: true } )}` : <CircularProgress size={14} />,
                    link: meeting?.meeting_type === 'class' ? `/faculty/online-classes` : meeting?.meeting_type === 'hybrid-class' ? `/faculty/hybrid-classes` : `/faculty/meetings?sessionType=${meeting?.meeting_type === "webinar" ? "webinar" : "discussion"}`
                },
                meeting?.title || <CircularProgress size={14} />
            ]} />}

            {/* Popups and Dialog Boxes */}
            {!loading && editMode === "access" && <MeetingAccessEditDialog handleClose={handleEditAccessDialogClose} meeting={meeting} open={editMode} setOpen={setEditMode} />}

            {!loading && editMode === "details" && <EditMeetingDetails handleClose={handleEditAccessDialogClose} meeting={meeting} open={editMode} setOpen={setEditMode} />}

            {addQuizMode && <AddQuizFromClassDialog fetchQuizzesInstances={() => getClasses( true )} state={Boolean( meeting.time_table_class_details )} room={meeting.time_table_class_details} setState={setAddQuizMode} meetingType={meeting.meeting_type} />}

            {Boolean( QuizStore.getClassQuizzes[meeting.time_table_id]?.[0] ) && <OnlineSessionQuizDetails openState={quizDetailsMode} quizInstanceDetails={classQuizInstanceDetails} handleClose={() => setQuizDetailsMode( false )} />}

            {["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) && <OnlineSessionQuizDetails openState={quizDetailsMode} quizInstanceDetails={onlineSessionQuizInstance} handleClose={() => setQuizDetailsMode( false )} />}

            {['hybrid-class'].includes( meeting.meeting_type ) && attendanceReviewDialogOpen && <HybridClassAttendanceConfirmDialog key={meeting_id} open={attendanceReviewDialogOpen} handleClose={() => setAttendanceReviewDialogOpen( false )} />}


            {showCopyDialog && <MeetingDetailsCopyDialog open={showCopyDialog} setOpen={setShowCopyDialog} meeting={meeting} />}

            {showDeleteMeetingConfirmationDialog && <ConfirmDialog waiting={deleting} message='All the meeting informations such as polls, chats and attendance will be deleted!' status={showDeleteMeetingConfirmationDialog} actionName="Yes! delete it" cancelAction={() => setShowDeleteMeetingConfirmationDialog( false )} confirmAction={deleteOnlineMeeting} />}

            {/* Main Content */}
            {!loading && meeting && !error && <Box flexGrow={pathname.match( /audit|admin/ ) ? 1 : "initial"} className="no-scrollbar" overflow={pathname.match( /audit|admin/ ) ? "auto" : "initial"} borderRadius={layout.borderRadius} bgcolor={palette.background.paper}>
                <Box display="flex" flexWrap="wrap" alignItems="flex-end" justifyContent="space-between" gap={layout.sectionsGap}>
                    <Box>
                        <Box display="flex" flexWrap="wrap" gap="5px">
                            {!meeting.ended && <CustomChip variant='filled' color={meeting.can_join ? "success" : meeting.ended ? "error" : !meeting.started ? "secondary" : ""} value={meeting.can_join ? "Active" : meeting.ended ? "Ended" : !meeting.started ? "Not yet started" : ""} />}
                            {["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) && <CustomChip variant='filled' value={meeting?.attendance_fetched ? "Attendance synced with ERP" : "Attendance yet to be synced"} color={meeting?.attendance_fetched ? "success" : "secondary"} tooltipTitle={meeting?.attendance_fetched ? `Attendance synced at ${dayjs( meeting?.attendance_fetched_at ).format( "LLL" )}` : 'Attendance sync status'} />}
                            <CustomChip value={meeting.meeting_type} tooltipTitle='Session Type' textStyles={{ textTransform: "uppercase" }} />
                            <CustomChip value={meeting.status || "Not Started"} tooltipTitle='Session Status' textStyles={{ textTransform: "uppercase" }} />
                            <CustomChip value={meeting.meeting_settings?.access_type?.replace( /_/g, " " )} tooltipTitle='Session Access Type' />
                        </Box>
                        <Typography marginTop="10px" fontSize="18px" fontWeight="700" component="h1" variant="h4">{meeting?.title}</Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap="10px">
                        {meeting.created_by === UserStore.getUser.user_id && <Button size='small' onClick={() => setShowCopyDialog( true )} startIcon={Icons.default.Info} disableElevation>
                            Copy Details
                        </Button>}
                        {meeting.created_by === UserStore.getUser.user_id && !['class', 'hybrid-class'].includes( meeting.meeting_type ) && <Button size='small' onClick={() => setEditMode( "details" )} variant='outlined' startIcon={Icons.default.EditIcon} disableElevation>
                            Edit
                        </Button>}
                        {( ["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) || ( meeting.created_by === UserStore.getUser.user_id && !['class', 'hybrid-class'].includes( meeting.meeting_type ) ) ) && <Button size='small' color='error' onClick={() => setShowDeleteMeetingConfirmationDialog( true )} variant='contained' startIcon={Icons.default.DeleteIconOutlined} disableElevation>
                            Delete
                        </Button>}
                    </Box>

                </Box>

                {!loading && meeting && meeting.meeting_type !== 'hybrid-class' && <Box marginTop="20px" bgcolor={palette.contentBg} padding="10px" border={border[1]} borderRadius={layout.borderRadius} >
                    <Box marginBottom="10px">
                        <Typography gutterBottom fontSize="14px"><strong>Note:</strong> The mic, camera, and screen-sharing settings below apply only to attendees. Hosts will always have access to all of these options. </Typography>
                    </Box>
                    <Box display="grid" gap="20px" width="100%" gridTemplateColumns={{ md: "repeat(4,1fr)", xs: "repeat(2,1fr)" }}>
                        {meeting.time_table_class_details?.subject_name_short && <CountCard label='Class Subject' icon={Icons.default.Subjects} value={meeting.time_table_class_details?.subject_name_short} />}
                        <CountCard label='Camera/Mic' icon={Icons.default.Devices} value={`${meeting.meeting_settings?.enable_video_feed ? "ON" : "OFF"}/${meeting.meeting_settings?.enable_microphone ? "ON" : "OFF"}`} />
                        <CountCard label='Screen Sharing' icon={Icons.default.PresentToAll} value={`${meeting.meeting_settings?.enable_screen_sharing ? "ENABLED" : "DISABLED"}`} />
                        {['class', 'hybrid-class'].includes( meeting?.meeting_type || "" ) && <CountCard label='Must complete Quiz' icon={Icons.default.QuizIcon} value={`${meeting.meeting_settings?.must_complete_quiz ? "YES" : "NO"}`} />}
                    </Box>
                </Box>}

                {!loading && meeting && meeting.meeting_type === 'hybrid-class' && <Box marginTop="20px" display="grid" gap="20px" width="100%" gridTemplateColumns={{ md: "repeat(4,1fr)", xs: "repeat(2,1fr)" }}>
                    <CountCard label='Videos Uploaded' highlightBorder={meetingRecordings.filter( v => v.type !== "class-video" ).length === 0} color={meetingRecordings.filter( v => v.type !== "class-video" ).length === 0 ? "error" : "primary"} icon={Icons.default.VideocamIcon} value={`${meetingRecordings.filter( v => v.type !== "class-video" ).length === 0 ? "Pending" : meetingRecordings.filter( v => v.type !== "class-video" ).length}`} />
                    {meeting.time_table_class_details?.subject_name_short && <CountCard label='Class Subject' icon={Icons.default.Subjects} value={meeting.time_table_class_details?.subject_name_short} />}
                    <CountCard label='Class For' icon={Icons.default.Group} value={meeting.time_table_class_details?.section_assignment_id ? `Section: ${meeting.time_table_class_details.section_short_name}` : `Batch: ${meeting.time_table_class_details.batch_short_name}`} />
                    <CountCard label='Must complete Quiz' icon={Icons.default.QuizIcon} value={`${meeting.meeting_settings?.must_complete_quiz ? "YES" : "NO"}`} />
                </Box>}

                <Box justifyContent="space-between" display="flex" gap="10px" alignItems="center" flexWrap="wrap" marginY="20px">
                    <Box display="flex" flexWrap="wrap" alignItems="center" gap="10px">

                        {/* {meeting.ended && ['class', 'hybrid-class'].includes( meeting.meeting_type ) && <Button size='small' color='primary' variant='contained' onClick={() => setAttendanceReviewDialogOpen( true )} endIcon={Icons.default.Sync} disableElevation>
                            {meeting.attendance_fetched ? "Update" : "Sync"} Attendance
                        </Button>} */}

                        {
                            meeting.can_join &&
                            (
                                meeting.meeting_type !== "hybrid-class" ||
                                (
                                    meeting.meeting_type === "hybrid-class" &&
                                    SettingsStore?.getUseSettings['hybrid_class_streaming_enabled']
                                )
                            ) &&

                            <Link className='default-link' to={`${window.origin}/${["webinar", "class"].includes( meeting.meeting_type ) ? meeting.meeting_type : "meeting"}/${meeting.id}`}>
                                <Button size='small' sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, ":disabled": { color: palette.grey[500], background: palette.grey[700] } }} variant='contained' endIcon={Icons.default.Login} disableElevation>
                                    {meeting.meeting_type === "hybrid-class" ? "Join and stream" : "Join"} {getTransformedMeetingType( meeting.meeting_type, { forDisplay: true } )}
                                </Button>

                            </Link>}

                        {!meeting.can_join && <Tooltip title={!meeting.started ? "Session Not Started" : meeting.ended ? "Session Ended" : ""}>
                            <Box component="span">
                                <Button size='small' disabled={true} sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, ":disabled": { color: palette.grey[500], background: palette.grey[700] } }} variant='contained' endIcon={Icons.default.OpenInNewIcon} disableElevation>
                                    {meeting.meeting_type === "hybrid-class" ? "Join and stream" : "Join"} {getTransformedMeetingType( meeting.meeting_type, { forDisplay: true } )}
                                </Button>
                            </Box>
                        </Tooltip>}

                        {meeting.created_by === UserStore.getUser.user_id && ['class', 'hybrid-class'].includes( meeting.meeting_type ) && !QuizStore.getClassQuizzes[meeting.time_table_id]?.length && dayjs( meeting.starts_at ).isSame( dayjs(), 'day' ) && <Button color='secondary' size='small' onClick={() => setAddQuizMode( true )} variant='outlined' endIcon={Icons.default.QuizIcon} disableElevation>
                            Assign Quiz
                        </Button>}
                        {['class', 'hybrid-class'].includes( meeting.meeting_type ) && meeting.created_by === UserStore.getUser.user_id && QuizStore.getClassQuizzes[meeting.time_table_id]?.length > 0 && QuizStore.getClassQuizzes[meeting.time_table_id]?.map( quiz => <Button key={quiz.id} size='small' onClick={() => setQuizDetailsMode( true )} variant='outlined' endIcon={Icons.default.QuizIcon} disableElevation>
                            View Quiz Details
                        </Button> )}

                        {["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) && <Button size='small' onClick={() => setQuizDetailsMode( true )} variant='outlined' endIcon={Icons.default.QuizIcon} disableElevation>
                            View Quiz Details
                        </Button>}
                        {['class', 'hybrid-class'].includes( meeting.meeting_type ) &&
                            !meeting.attendance_fetched &&
                            ["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) &&
                            dayjs().isAfter( dayjs( meeting.ends_at ) ) &&
                            !dayjs().isSame( dayjs( meeting.ends_at ), 'day' ) &&

                            <Button size='small' onClick={retryAttendanceSync} variant='outlined' endIcon={syncingAttendance ? <CircularProgress size={14} /> : Icons.default.PeopleAltIcon} disabled={syncingAttendance} disableElevation>
                                Sync Attendance
                            </Button>}

                        {meeting.created_by === UserStore.getUser.user_id && !['class', 'hybrid-class'].includes( meeting.meeting_type ) && <Button size='small' onClick={() => setEditMode( "access" )} variant='outlined' endIcon={Icons.default.Share} disableElevation>
                            View {getTransformedMeetingType( meeting.meeting_type, { forDisplay: true } )} access
                        </Button>}
                    </Box>
                </Box>

                {!error && meeting && <Box marginTop="10px" display="flex" gap="10px" alignItems="center" flexWrap='wrap'>
                    <Typography padding="5px 10px" bgcolor={palette.contentBg} borderRadius="5px" border={border[1]} fontSize="12px">{formatDate( meeting?.starts_at, { format: "MMM, D hh:mm A" } )}</Typography>
                    <Typography>to</Typography>
                    <Typography padding="5px 10px" bgcolor={palette.contentBg} borderRadius="5px" border={border[1]} fontSize="12px">{formatDate( meeting?.ends_at, { format: "MMM, D hh:mm A" } )}</Typography>
                </Box>}

                {
                    meeting.meeting_type === "hybrid-class" &&
                    !loadingRecordings &&
                    !recordingsFecthError &&
                    !error &&
                    ( meetingRecordings.filter( v => v.type !== "class-video" ).length === 0 || SettingsStore?.getUseSettings['hybrid_classes_multi_video_uploads_enabled'] ) &&
                    dayjs( dayjs( meeting.ends_at ).format( "YYYY-MM-DD" ) ).hour( maxExtendTimeDetails.hour24 ) > dayjs() &&

                    <Box marginTop="20px">
                        <Box bgcolor={palette.contentBg} gap="10px" display="flex" flexDirection="column" alignItems="center" border={`2px dashed ${palette.borderColor}`} padding="20px" borderRadius={layout.borderRadius} >

                            <Typography fontSize="48px" display="flex" alignItems="center">{Icons.default.VideocamIcon}</Typography>
                            <Typography gutterBottom variant='subtitle2' component="p">Add recorded videos for students to watch this will be used to as attendance criteria for offline students.</Typography>

                            <Box display="flex" gap={layout.cardsGap} alignItems="center" flexWrap="wrap" >
                                <AddNewRecordingButton quiz={classQuizInstanceDetails} classDetails={meeting.time_table_class_details} setTab={setTabValue} styles={{ boxShadow: "none" }} buttonSize="large" availableVideos={meetingRecordings} />
                            </Box>
                        </Box>
                    </Box>}

                {!error && <Box marginTop="20px" overflow="auto" display="flex" flexDirection="column" borderRadius="5px" border={border[1]} >

                    <CustomTabs tabs={tabs} handleTabChange={setTabValue} value={tabValue} variant='stretched' styles={{ width: "100%" }} />

                    {/* Tab Panels */}

                    <Box overflow="auto" flexGrow={1}>
                        {tabValue === "hosts" && <Box padding="10px">
                            <CustomTable data={meeting?.hosts || []} columns={hostsTableColumns} />
                            {/* <Box display="flex" gap="10px" alignItems="center" flexWrap='wrap'>
                                {meeting?.hosts?.map( host => (
                                    <Box padding="5px 10px" borderRadius="5px" bgcolor={palette.contentBg} key={host.user_id}>
                                        <Typography fontSize="14px">{host?.user_name}</Typography>
                                        <Typography fontSize="10px">{host?.user_email}</Typography>
                                    </Box>
                                ) )}
                            </Box> */}
                        </Box>}
                        {tabValue === "recordings" && <OnlineSessionRecordingsDetails setTabValue={setTabValue} />}
                        {tabValue === "feedback" && <OnlineSessionFeedbacksDetails />}
                        {tabValue === "polls" && <OnlineSessionPolls />}
                        {tabValue === "attendance" && <OnlineSessionAttendanceDetails meeting={meeting} />}
                    </Box>

                </Box>}
            </Box>}

            {loading && !error && <DataLoadingSpinner waitingMessage="Loading meeting details..." />}
            {error && <TryAgainMessageBlock getData={fetchMeeting} loading={refreshing} code={error?.code} err={error?.message} />}

        </Box >
    )
} )

export default OnlineMeetingDetails


/* {tabValue === "vbnotes" && <Box padding="10px">
                        <Box fontSize="36px" gap="20px" color={palette.common.font} bgcolor={palette.contentBg} display="flex" flexDirection="column" alignItems="center" justifyContent="center" padding="20px" borderRadius={layout.borderRadius} >
                            {Icons.default.Clock}
                            <Typography variant='subtitle2' component="p" textAlign="center">Coming soon!</Typography>
                        </Box>
                    </Box>} */